import React, { ReactElement, useState, useContext } from 'react';
import styled from 'styled-components/macro';
import { FormsContext } from '../../context/FormsContext';
import {
  CheckBoxInputIcon,
  DateInputIcon,
  DropDownInputIcon,
  MultipleChoiceInputIcon,
  RatingInputIcon,
  TextInputIcon,
  TimeInputIcon,
  MultiLineTextInputIcon,
  TextBlockInputIcon,
  SignatureIcon,
} from '../../assets/icons';
import Tooltip from 'shared/components/Tooltip';
import { Box, Button, Heading, HStack } from '@televet/kibble-ui';
import { FormQuestionType } from '@televet/shared-types';
import { useTracking } from 'shared/providers/TrackingProvider';
import { FormsEvents } from 'shared/providers/TrackingProvider/EventNames';

interface ITools {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: { name: string; description: string; data?: any };
}
export const Tools: ITools = {
  textInput: {
    name: 'Text Input',
    description: '',
    data: {
      type: FormQuestionType.TextInput,
      prompt: '',
    },
  },
  multiLineTextInput: {
    name: 'Multi Line Text Input',
    description: '',
    data: {
      type: FormQuestionType.MultiLineTextInput,
      prompt: '',
    },
  },
  multipleSelection: {
    name: 'Multiple Selection',
    description: '',
    data: {
      type: FormQuestionType.MultipleSelection,
      prompt: '',
      options: ['', ''],
    },
  },
  dropDown: {
    name: 'Drop Down',
    description: '',
    data: {
      type: FormQuestionType.DropDown,
      prompt: '',
      options: ['', ''],
    },
  },
  petPicker: {
    name: 'Patient Picker',
    description: '',
    data: {
      type: FormQuestionType.PetPicker,
      prompt: '',
      options: [],
    },
  },
  checkbox: {
    name: 'Checkbox',
    description: '',
    data: {
      type: FormQuestionType.Checkbox,
      prompt: '',
      defaultValue: false,
    },
  },

  textBlock: {
    name: 'Text Block',
    description: '',
    data: {
      type: FormQuestionType.TextBlock,
      prompt: '',
      noAnswer: true,
    },
  },

  dateInput: {
    name: 'Date Input',
    description: '',
    data: {
      type: FormQuestionType.DateInput,
      prompt: '',
    },
  },
  timeInput: {
    name: 'Time Input',
    description: '',
    data: {
      type: FormQuestionType.TimeInput,
      prompt: '',
    },
  },
  ratingInput: {
    name: 'Rating Input',
    description: '',
    data: {
      type: FormQuestionType.RatingInput,
      prompt: '',
      minValue: '',
      maxValue: '',
    },
  },
  signatureInput: {
    name: 'Signature Input',
    description: '',
    data: {
      type: FormQuestionType.SignatureInput,
      prompt: '',
      minValue: '',
      maxValue: '',
      isRequired: true,
    },
  },
};

interface IAddQuestion {
  addingIndex: number;
}

const AddQuestion = ({ addingIndex }: IAddQuestion): ReactElement => {
  const [isActive, setIsActive] = useState(false);
  const { dispatch, clinicWidgetRequestTypes } = useContext(FormsContext);
  const { trackEvent } = useTracking();

  const renderIcon = (type: FormQuestionType): JSX.Element | undefined => {
    switch (type) {
      case FormQuestionType.TextInput:
        return <TextInputIcon />;
      case FormQuestionType.MultiLineTextInput:
        return <MultiLineTextInputIcon />;
      case FormQuestionType.MultipleSelection:
        return <MultipleChoiceInputIcon />;
      case FormQuestionType.DropDown:
      case FormQuestionType.PetPicker:
        return <DropDownInputIcon />;
      case FormQuestionType.Checkbox:
        return <CheckBoxInputIcon />;
      case FormQuestionType.DateInput:
        return <DateInputIcon />;
      case FormQuestionType.TimeInput:
        return <TimeInputIcon />;
      case FormQuestionType.RatingInput:
        return <RatingInputIcon />;
      case FormQuestionType.TextBlock:
        return <TextBlockInputIcon />;
      case FormQuestionType.SignatureInput:
        return <StyledSignatureIcon />;
      default:
        break;
    }
  };

  const handleAddOption = (tool: string): void => {
    setIsActive(false);
    dispatch({
      type: 'ADD_QUESTION_AT_INDEX',
      payload: { question: Tools[tool].data, index: addingIndex },
    });
  };

  if (!isActive) {
    return (
      <InactiveWrapper>
        <Line />
        <Button
          onClick={(): void => {
            setIsActive(true);
          }}
          data-mixpanel-name="Add form question button"
          data-testid="add-question-button"
          size="xs"
          variant="secondarySubtle"
          iconName="plus"
        >
          Add New Question
        </Button>
        <Line />
      </InactiveWrapper>
    );
  }

  return (
    <Box bg="background.primaryAlpha" p={2} my={5} borderRadius="lg">
      <HStack justify="center" align="flex-start" pos="relative" my={5}>
        <Heading size="sm">Add a Question</Heading>
        <Button
          data-mixpanel-name="Cancel adding form question button"
          data-testid="cancel-add-question-button"
          onClick={(): void => setIsActive(false)}
          iconName="close"
          variant="ghostNeutral"
          size="sm"
          pos="absolute"
          top="-10px"
          right={1}
        />
      </HStack>
      <Options>
        {Object.keys(Tools).map((tool, index) => {
          const isWidgetRequestForm = !!clinicWidgetRequestTypes?.length;
          const isDisabled =
            isWidgetRequestForm && (tool === FormQuestionType.RatingInput || tool === FormQuestionType.SignatureInput);
          return (
            <Tooltip
              key={`add-tool-index-${index}`}
              content="This question type isn't yet supported for Website Widget forms."
              disabled={!isDisabled}
            >
              <Option
                onClick={(): void => {
                  if (isDisabled) return;
                  trackEvent(FormsEvents.QuestionAdded, { questionType: Tools[tool].name });
                  handleAddOption(tool);
                }}
                $isDisabled={isDisabled}
                data-mixpanel-name={`Form ${tool} option button`}
                data-testid={`form-${tool}-option"`}
              >
                <Circle>{renderIcon(Tools[tool].data.type)}</Circle>
                <ToolName>{Tools[tool].name}</ToolName>
              </Option>
            </Tooltip>
          );
        })}
      </Options>
    </Box>
  );
};

export default AddQuestion;

const InactiveWrapper = styled.div`
  display: flex;
  width: 100%;
  margin: 40px 0 40px 0;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const Line = styled.div`
  border-color: #ebedf0;
  border-style: solid;
  border-width: 1px;
  flex: 1;
  height: 1px;
`;

const Options = styled.div`
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
`;

interface IOptionProps {
  $isDisabled: boolean;
}

const Option = styled.div<IOptionProps>`
  align-items: center;
  color: hsl(233deg 90% 10%);
  cursor: pointer;
  display: flex;
  flex-basis: 25%;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 40px;
  align-items: center;
  text-align: center;
  border: 1px solid transparent;
  border-radius: 5px;
  padding: 5px;
  background: transparent;
  opacity: ${({ $isDisabled }): string => ($isDisabled ? '0.5' : '1.0')};

  &:hover {
    border-color: ${({ $isDisabled }): string => ($isDisabled ? 'transparent' : '#ddd')};
  }

  &[disabled] {
    opacity: 0.5;
    border-color: transparent;
  }
`;

const Circle = styled.div`
  display: flex;
  height: 32px;
  width: 32px;
  background-color: #e0f1f4;
  border-radius: 16px;
  margin-bottom: 5px;
  justify-content: center;
  align-items: center;
`;

const ToolName = styled.h5`
  font-size: 14px;
  text-align: center;
`;

const StyledSignatureIcon = styled(SignatureIcon)`
  width: 18px;
  & * {
    fill: var(--chakra-colors-text-brand);
  }
`;
